/*
 * Formularios del ciclo comercial: redactar propuesta, nuevo cobro, arrancar
 * proyecto. Se montan desde ProveedorFormularios igual que los demás.
 */

import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { ErrorApi, sesion } from "../api/cliente";
import { useArrancarProyecto, useCrearPropuesta, useNuevoPago, useVista } from "../api/hooks";
import { EQUIPO, type CuentaDetalle, type Direccion } from "../api/tipos";
import { Campo, Modal, Seg, grid2 } from "../ui/Modal";
import { fmtFecha, fmtMXN, hoyISO } from "../ui/primitivas";
import { useAvisos } from "./Aviso";

function mensajeError(e: unknown): string {
  return e instanceof ErrorApi ? e.message : "No se pudo guardar. Revisa la conexión e inténtalo otra vez.";
}

function sumarDias(iso: string, n: number): string {
  const d = new Date(iso + "T12:00:00");
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// ─── Propuesta ──────────────────────────────────────────────────────────────
//
// Se prellena desde el cierre D5 del expediente: indicador, baseline, quién valida.
// Lo que el Taller acordó no se vuelve a teclear.

export function FormPropuesta({ c, casoId, cerrar }: { c: CuentaDetalle; casoId: string | null; cerrar: () => void }) {
  const { avisar } = useAvisos();
  const crear = useCrearPropuesta(c.id);
  const { data: vista } = useVista(casoId);
  const cierre = vista?.cierre;
  const hoy = hoyISO();
  const [d, setD] = useState({
    nombre: cierre?.oportunidad?.titulo ?? "", encargo: cierre?.oportunidad?.una_linea ?? "",
    indicador: cierre?.indicador?.kpi ?? "", unidad: cierre?.baseline?.unidad ?? "%",
    baseline: cierre?.baseline ? String(cierre.baseline.numero) : "", meta: "", direccion: "baja" as Direccion,
    fuente: cierre?.indicador?.fuente_datos ?? cierre?.baseline?.fuente.reporte ?? "",
    valida: cierre?.baseline?.validado_por ?? (c.contacto !== "Por confirmar" ? c.contacto : ""),
    base: cierre?.terminos?.base_fija ?? 240000, riesgo_pct: cierre?.terminos?.fee_at_risk_pct ?? 20,
    pagos: [
      { concepto: "Anticipo", pct: 40, vence: sumarDias(hoy, 14) },
      { concepto: "Arranque de piloto", pct: 30, vence: sumarDias(hoy, 42) },
      { concepto: "Cierre", pct: 30, vence: sumarDias(hoy, 70) },
    ],
  });
  const u = <K extends keyof typeof d>(k: K) => (v: (typeof d)[K]) => setD((x) => ({ ...x, [k]: v }));
  const up = (i: number, k: "concepto" | "pct" | "vence", v: string) => setD((x) => ({ ...x, pagos: x.pagos.map((r, j) => (j === i ? { ...r, [k]: k === "pct" ? Number(v) : v } : r)) }));
  const [error, setError] = useState("");
  const pctSum = d.pagos.reduce((s, r) => s + (Number(r.pct) || 0), 0);
  const base = Number(d.base) || 0, riesgo = Math.round((base * (Number(d.riesgo_pct) || 0)) / 100);
  const ok = d.nombre.trim() !== "" && d.indicador.trim() !== "" && base > 0 && pctSum === 100;

  const guardar = async (enviar: boolean) => {
    if (!ok) { setError(pctSum !== 100 ? "Los pagos deben sumar 100 %." : "Faltan el encargo, el indicador o los honorarios."); return; }
    try {
      const p = await crear.mutateAsync({
        nombre: d.nombre, encargo: d.encargo, indicador: d.indicador, unidad: d.unidad,
        baseline: d.baseline === "" ? null : Number(d.baseline), meta: d.meta === "" ? null : Number(d.meta),
        direccion: d.direccion, fuente: d.fuente, valida: d.valida, base, riesgo_pct: Number(d.riesgo_pct) || 0,
        pagos: d.pagos.map((r) => ({ concepto: r.concepto, pct: Number(r.pct), vence: r.vence || null })), caso_id: casoId, enviar,
      });
      cerrar();
      avisar(enviar ? `Propuesta v${p.version} enviada a ${c.empresa}` : `Propuesta v${p.version} guardada como borrador`);
    } catch (err) { setError(mensajeError(err)); }
  };

  return (
    <Modal ancho={640} titulo="Redactar propuesta" sub={`${c.empresa} · la hoja de un solo número: encargo, indicador, baseline y términos`} onCerrar={cerrar}
      pie={<>
        <span style={{ marginRight: "auto", fontSize: 12.5, color: pctSum === 100 ? "var(--faint)" : "var(--risk)" }}>Pagos suman {pctSum}%{pctSum !== 100 ? " · deben sumar 100%" : ""}</span>
        <button type="button" className="btn btn-secundario" onClick={cerrar}>Cancelar</button>
        <button type="button" className="btn btn-secundario" disabled={!ok || crear.isPending} onClick={() => guardar(false)}>Guardar borrador</button>
        <button type="button" className="btn btn-primario" disabled={!ok || crear.isPending} onClick={() => guardar(true)}>Compartir con el cliente</button>
      </>}>
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); guardar(true); }} style={grid2}>
        <Campo label="Encargo" span error={error}><input autoFocus value={d.nombre} onChange={(e) => u("nombre")(e.target.value)} placeholder="Nombre del primer encargo" /></Campo>
        <Campo label="Qué se construye" span><textarea rows={2} value={d.encargo} onChange={(e) => u("encargo")(e.target.value)} placeholder="En lenguaje llano, una o dos líneas." /></Campo>
        <Campo label="Indicador" span><input value={d.indicador} onChange={(e) => u("indicador")(e.target.value)} placeholder="El número que se mueve" /></Campo>
        <Campo label="Baseline"><input type="number" step="any" value={d.baseline} onChange={(e) => u("baseline")(e.target.value)} placeholder="Hoy" /></Campo>
        <Campo label="Meta"><input type="number" step="any" value={d.meta} onChange={(e) => u("meta")(e.target.value)} placeholder="En 30 días" /></Campo>
        <Campo label="Unidad"><input value={d.unidad} onChange={(e) => u("unidad")(e.target.value)} placeholder="% · h · piezas" /></Campo>
        <Campo label="Dirección"><Seg value={d.direccion} onChange={u("direccion")} opciones={[["baja", "Debe bajar"], ["sube", "Debe subir"]]} /></Campo>
        <Campo label="Fuente del dato" span><input value={d.fuente} onChange={(e) => u("fuente")(e.target.value)} placeholder="Reporte, sistema o bitácora exacta" /></Campo>
        <Campo label="Quién valida la lectura" span><input value={d.valida} onChange={(e) => u("valida")(e.target.value)} /></Campo>
        <Campo label="Honorarios base" pista="MXN"><input type="number" step="1000" value={d.base} onChange={(e) => u("base")(Number(e.target.value))} /></Campo>
        <Campo label="Fee-at-risk %" pista={`${fmtMXN(riesgo)} sujeto al indicador`}><input type="number" min={0} max={50} value={d.riesgo_pct} onChange={(e) => u("riesgo_pct")(Number(e.target.value))} /></Campo>
        <div style={{ gridColumn: "1 / -1" }}>
          <span className="tag11" style={{ color: "var(--muted)", display: "block", marginBottom: 6 }}>Esquema de pagos <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400, color: "var(--faint)" }}>sobre la base fija · se vuelven cobros en CxC al aceptar</span></span>
          <div className="campo" style={{ display: "grid", gridTemplateColumns: "1fr 72px 150px 100px", gap: 8, alignItems: "center" }}>
            {d.pagos.map((r, i) => (
              <span key={i} style={{ display: "contents" }}>
                <input value={r.concepto} onChange={(e) => up(i, "concepto", e.target.value)} />
                <input type="number" min={0} max={100} value={r.pct} onChange={(e) => up(i, "pct", e.target.value)} />
                <input type="date" value={r.vence} onChange={(e) => up(i, "vence", e.target.value)} />
                <span className="mono" style={{ textAlign: "right", color: "var(--muted)" }}>{fmtMXN((base * (Number(r.pct) || 0)) / 100)}</span>
              </span>
            ))}
          </div>
        </div>
      </form>
    </Modal>
  );
}

// ─── Nuevo cobro ────────────────────────────────────────────────────────────

export function FormPago({ c, proyectoId, cerrar }: { c: CuentaDetalle; proyectoId?: string; cerrar: () => void }) {
  const { avisar } = useAvisos();
  const nuevo = useNuevoPago(c.id);
  const proys = c.proyectos;
  const [d, setD] = useState({ proyecto_id: proyectoId ?? proys[0]?.id ?? "", proyecto_nombre: "", concepto: "", monto: "", vence: sumarDias(hoyISO(), 30), condicionado: false, facturar: true });
  const u = <K extends keyof typeof d>(k: K) => (v: (typeof d)[K]) => setD((x) => ({ ...x, [k]: v }));
  const [error, setError] = useState("");
  const ok = d.concepto.trim() !== "" && Number(d.monto) > 0 && (d.proyecto_id !== "" || d.proyecto_nombre.trim() !== "");
  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    if (!ok) { setError("Concepto, monto y a qué corresponde."); return; }
    try {
      await nuevo.mutateAsync({ concepto: d.concepto, monto: Number(d.monto), vence: d.vence, proyecto_id: d.proyecto_id || null, proyecto_nombre: d.proyecto_nombre, condicionado: d.condicionado, facturar: d.facturar });
      cerrar();
      avisar(`Cobro de ${fmtMXN(Number(d.monto))} a ${c.empresa} registrado${d.facturar ? " y facturado" : ""} · visible en CxC`);
    } catch (err) { setError(mensajeError(err)); }
  };
  return (
    <Modal titulo="Nuevo cobro" sub={`${c.empresa} · aparece en CxC como pendiente hasta marcarlo cobrado`} onCerrar={cerrar}
      pie={<><button type="button" className="btn btn-secundario" onClick={cerrar}>Cancelar</button><button form="f-pago" className="btn btn-primario" disabled={!ok || nuevo.isPending}>Registrar cobro</button></>}>
      <form id="f-pago" onSubmit={enviar} style={grid2}>
        {proys.length > 0 && <Campo label="Proyecto" span><select value={d.proyecto_id} onChange={(e) => u("proyecto_id")(e.target.value)}>{proys.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}<option value="">Otro / sin proyecto</option></select></Campo>}
        {!d.proyecto_id && <Campo label="Concepto general" span><input value={d.proyecto_nombre} onChange={(e) => u("proyecto_nombre")(e.target.value)} placeholder="Diagnóstico operativo, taller…" /></Campo>}
        <Campo label="Concepto del cobro" span error={error}><input autoFocus value={d.concepto} onChange={(e) => u("concepto")(e.target.value)} placeholder="Anticipo 40% · Pago único · Cierre" /></Campo>
        <Campo label="Monto" pista="MXN"><input type="number" step="1000" value={d.monto} onChange={(e) => u("monto")(e.target.value)} /></Campo>
        <Campo label="Vence"><input type="date" value={d.vence} onChange={(e) => u("vence")(e.target.value)} /></Campo>
        <div style={{ gridColumn: "1 / -1", display: "flex", gap: 20, flexWrap: "wrap", fontSize: 13.5 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}><input type="checkbox" checked={d.facturar} onChange={(e) => u("facturar")(e.target.checked)} /> Emitir factura (CFDI) ahora</label>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}><input type="checkbox" checked={d.condicionado} onChange={(e) => u("condicionado")(e.target.checked)} /> Condicionado al indicador</label>
        </div>
      </form>
    </Modal>
  );
}

// ─── Arrancar proyecto ──────────────────────────────────────────────────────

export function FormArrancar({ cuentaId, proyectoId, nombre, empresa, cerrar }: { cuentaId: string; proyectoId: string; nombre: string; empresa: string; cerrar: () => void }) {
  const { avisar } = useAvisos();
  const nav = useNavigate();
  const arrancar = useArrancarProyecto(cuentaId, proyectoId);
  const [inicio, setInicio] = useState(sumarDias(hoyISO(), 3));
  const yo = sesion.quien() || "p-pab";
  const [error, setError] = useState("");
  const go = async () => {
    try {
      await arrancar.mutateAsync({ inicio, comercial_id: EQUIPO.find((p) => p.id === yo)?.id ?? "p-pab" });
      cerrar();
      avisar(`«${nombre}» arranca ${fmtFecha(inicio, true)} · P1 Arranque`);
      nav(`/proyectos/${proyectoId}`);
    } catch (err) { setError(mensajeError(err)); }
  };
  return (
    <Modal titulo="Arrancar proyecto" sub={`${nombre} · ${empresa}`} onCerrar={cerrar}
      pie={<><button className="btn btn-secundario" onClick={cerrar}>Cancelar</button><button className="btn btn-primario" disabled={arrancar.isPending} onClick={go}>Arrancar</button></>}>
      <div style={grid2}>
        <Campo label="Inicio de P1 · Arranque" error={error}><input autoFocus type="date" value={inicio} min={hoyISO()} onChange={(e) => setInicio(e.target.value)} /></Campo>
        <div style={{ fontSize: 12.5, color: "var(--muted)", lineHeight: 1.5, alignSelf: "end" }}>Calendariza P1–P5 en ~9 semanas y abre la ventana de 30 días del indicador con el piloto. Se ajusta después en Cronograma.</div>
      </div>
    </Modal>
  );
}
